import { useState } from 'react';
import { FieldTimeOutlined } from '@ant-design/icons';

import { Button, Dialog } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { ngayGioVN } from '../../../../lib/dinh-dang';
import { laMaLuotNative } from '../../nghiep-vu/lich-su';
import { loaiSuCo, mucDoSuCo, nhanDanhMuc } from '../../nghiep-vu/su-co';
import type { SuCo } from '../../types';
import { HopDongThoiGian } from '../lich-su/HopDongThoiGian';
import { KhoiBaoCaoLoi } from '../lich-su/KhoiBaoCaoLoi';
import { BadgeLoaiLoi, BadgeMucDo, BadgeNguon, BadgeTrangThaiLuot, TruongThongTin, useLoiNgan } from '../lich-su/nhan';

/**
 * Chi tiết một sự cố. Lỗi Robot báo về kèm cả báo cáo lỗi (bước, ảnh, video, log); lượt FOXAI gửi lệnh rồi hỏng
 * (`RUN-…`) mở được dòng thời gian của lượt đó.
 */
export function HopChiTietSuCo({ suCo, onClose }: { suCo: SuCo; onClose: () => void }) {
  const { t, locale } = useT();
  const loiNgan = useLoiNgan();
  const [xemDongThoiGian, setXemDongThoiGian] = useState(false);
  const nguonNgan = loiNgan(suCo.source);
  const maLuot = suCo.origin === 'foxai' ? suCo.id : laMaLuotNative(suCo.executionId) ? suCo.executionId : '';
  const coBaoCao = suCo.origin === 'robot' || (suCo.origin === 'foxai' && laMaLuotNative(maLuot));

  return (
    <Dialog
      open
      onClose={onClose}
      width={suCo.origin === 'robot' ? 1000 : 760}
      title={
        <span className="flex flex-wrap items-center gap-2">
          {t('rpaSuCo.chiTietTieuDe')}
          <BadgeNguon nguon={suCo.origin} />
          <BadgeTrangThaiLuot status={suCo.status} />
        </span>
      }
      footer={
        <div className="flex flex-wrap justify-end gap-2">
          {laMaLuotNative(maLuot) && (
            <Button variant="outline" size="md" icon={<FieldTimeOutlined />} onClick={() => setXemDongThoiGian(true)}>
              {t('rpaBaoCao.xemDongThoiGian')}
            </Button>
          )}
          <Button variant="outline" size="md" onClick={onClose}>
            {t('chung.dong')}
          </Button>
        </div>
      }
    >
      <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
        <TruongThongTin nhan={t('rpaSuCo.ct.thoiGian')}>{ngayGioVN(suCo.createdAt, locale)}</TruongThongTin>
        <TruongThongTin nhan={t('rpaSuCo.ct.mucDo')}>
          <BadgeMucDo mucDo={mucDoSuCo(suCo)} />
        </TruongThongTin>
        <TruongThongTin nhan={t('rpaSuCo.ct.project')}>{[suCo.projectName, suCo.projectId].filter(Boolean).join(' · ')}</TruongThongTin>
        <TruongThongTin nhan={t('rpaSuCo.ct.loai')}>
          <BadgeLoaiLoi loai={loaiSuCo(suCo)} />
        </TruongThongTin>
        <TruongThongTin nhan={t('rpaSuCo.ct.maLoi')}>{nhanDanhMuc(suCo)}</TruongThongTin>
        <TruongThongTin nhan={t('rpaSuCo.ct.nhomLoi')}>{suCo.errorGroup}</TruongThongTin>
        <TruongThongTin nhan={t('rpaSuCo.ct.workflow')}>{suCo.name || suCo.workflowId}</TruongThongTin>
        <TruongThongTin nhan={t('rpaSuCo.ct.may')}>{suCo.machineName}</TruongThongTin>
        {/* Câu ngắn kèm mã gốc ("Gọi Robot thất bại · OPENRPA_INVOKE_FAILED") để còn tra cứu. */}
        <TruongThongTin nhan={suCo.origin === 'robot' ? t('rpaSuCo.ct.buoc') : t('rpaSuCo.ct.nguonLoi')}>
          {nguonNgan && nguonNgan !== suCo.source ? `${nguonNgan} · ${suCo.source}` : suCo.source}
        </TruongThongTin>
        <TruongThongTin nhan={t('rpaSuCo.ct.id')}>
          {suCo.id && <code className="break-all font-mono text-xs">{suCo.id}</code>}
        </TruongThongTin>
      </dl>
      <p className="mb-1 mt-4 text-xs text-content-muted">{t('rpaSuCo.ct.loiDayDu')}</p>
      {suCo.message ? (
        <>
          <p className="mb-2 text-sm font-medium text-danger-700">{loiNgan(suCo.message)}</p>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words rounded-lg bg-subtle px-3 py-2 font-mono text-xs text-content-secondary">
            {suCo.message}
          </pre>
        </>
      ) : (
        <p className="text-sm text-content-muted">{t('rpaSuCo.khongCoThongBao')}</p>
      )}
      {coBaoCao && (
        <div className="mt-4">
          <KhoiBaoCaoLoi instanceId={suCo.instanceId ?? ''} runCode={maLuot} />
        </div>
      )}
      {xemDongThoiGian && <HopDongThoiGian runCode={maLuot} onClose={() => setXemDongThoiGian(false)} />}
    </Dialog>
  );
}
